/**
 * Layer 1 - the opening screen.
 *
 * Every figure here is read from `summary.json` by path. Not one is computed:
 * BUILD-SPEC 2 makes that structural, and the generated contract turns a
 * missing field into a compile error rather than a NaN on a phone.
 *
 * In its own module so it can be rendered against the real published summary in
 * a test - `tests/glance.test.tsx` uses the harness's golden file, so the chain
 * from frozen ledger to rendered DOM is checked in one go.
 */
import type { Summary } from "./contracts/index.js";
import { money, monthName, pct, signedPct } from "./format.js";

export { money };

export function Glance({
  summary,
  queueDepth,
  onQueueTap,
}: {
  summary: Summary | null;
  queueDepth: number;
  onQueueTap?: () => void;
}) {
  if (!summary) {
    return (
      <div class="glance empty">
        <h2>Nothing published yet</h2>
        <p class="muted">
          Photograph receipts here. When the queue is worth clearing, run the flow on your
          PC and the numbers appear.
        </p>
        <p class="big">{queueDepth}</p>
        <p class="muted">waiting to be processed</p>
      </div>
    );
  }

  const g = summary.glance;
  const cur = summary.base_currency;
  const delta = g.month.delta;
  // A presentation label, not a figure: is the headline month still running on
  // this device's clock? If so, the comparison below is partial-vs-complete
  // and says so, instead of always looking like a win until it isn't.
  const now = new Date();
  const localMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const inProgress = g.month.key === localMonth;
  const priorName = g.month.prior_key ? monthName(g.month.prior_key) : "the month before";

  return (
    <div class="glance">
      <section class="hero card">
        <div class="monthline">
          <h2>{monthName(g.month.key)}</h2>
          {inProgress && <span class="tag">in progress</span>}
        </div>
        <p class="big">{money(g.month.total, cur)}</p>
        {delta !== null && delta !== undefined && (
          <p class={delta > 0 ? "up" : delta < 0 ? "down" : "muted"}>
            {delta === 0
              ? `Level with ${priorName}`
              : `${money(Math.abs(delta), cur)} ${delta > 0 ? "more" : "less"} than ${priorName}`}
            {g.month.delta_pct !== null && g.month.delta_pct !== undefined && delta !== 0 && (
              <span class="muted"> · {signedPct(g.month.delta_pct)}</span>
            )}
          </p>
        )}
        {inProgress && delta !== null && delta !== undefined && (
          <p class="muted small">Partial month, compared against all of {priorName}.</p>
        )}
        <p class="muted small">{g.month.receipts} receipts</p>
      </section>

      <section class="hero card savings">
        <h3>Saved</h3>
        <p class="big">{money(g.savings.saved, cur)}</p>
        <p class="muted small">
          {(g.savings.rate * 100).toFixed(1)}% of {money(g.savings.gross, cur)}
        </p>
        <p class="muted small">
          deals {money(g.savings.item_deals, cur)} · receipt discounts{" "}
          {money(g.savings.receipt_discounts, cur)}
        </p>
      </section>

      <div class="grid">
        <div>
          <h3>Year to date</h3>
          <p>{money(g.ytd.total, cur)}</p>
          <p class="muted small">{g.ytd.receipts} receipts</p>
        </div>
        <button type="button" class="cell" onClick={onQueueTap} disabled={!onQueueTap}>
          <h3>Queue</h3>
          <p>{queueDepth}</p>
          <p class="muted small">waiting{onQueueTap ? " › " : ""}</p>
        </button>
        {g.biggest_month && (
          <div>
            <h3>Biggest month</h3>
            <p>{money(g.biggest_month.total, cur)}</p>
            <p class="muted small">{monthName(g.biggest_month.key)}</p>
          </div>
        )}
        {g.avg_active_month !== undefined && (
          <div>
            <h3>Average month</h3>
            <p>{money(g.avg_active_month, cur)}</p>
            <p class="muted small">over {g.active_months} active</p>
          </div>
        )}
        {summary.inflation && summary.inflation.overall !== null && (
          <div>
            <h3>Your inflation</h3>
            <p class={summary.inflation.overall > 0 ? "up" : summary.inflation.overall < 0 ? "down" : ""}>
              {signedPct(summary.inflation.overall)}
            </p>
            <p class="muted small">
              {summary.inflation.products} repeat products · {pct(summary.inflation.coverage)} of spend
            </p>
          </div>
        )}
      </div>

      <section>
        <h3>Where it goes</h3>
        <ul class="bars">
          {summary.stores.slice(0, 6).map((s) => (
            <li key={s.name}>
              <span class="label">{s.name}</span>
              <span class="bar-track">
                <span class="bar" style={{ width: `${s.share * 100}%` }} />
              </span>
              <span class="value">
                {money(s.total, cur)} <span class="muted">{pct(s.share)}</span>
              </span>
            </li>
          ))}
        </ul>
      </section>

      <p class="footnote small">
        Every figure here is computed on your PC and published. This screen formats; it
        never calculates.
      </p>
    </div>
  );
}
